// Account-scoped client caches (WP-19).
//
// Every localStorage cache that holds account-specific data must be keyed by the
// signed-in user id so that account B on a shared browser can never hydrate from
// account A's cached state. Signed-out state hydrates nothing — we never read or
// write an unscoped account key, and we purge the previous user's caches on
// identity change / sign-out.
//
// This mirrors the established per-user key pattern in appRecovery.js and
// useUserPreferences.js (`<prefix>:<userId>`).

// Caches that hold account-specific data and must be scoped + purged on identity
// change. Each entry is the key prefix INCLUDING its trailing separator, so the
// full key is `${prefix}${userId}`.
export const TRACKED_DAS_CACHE_PREFIX = 'ss_tracked:';
export const WATCH_ZONES_CACHE_PREFIX = 'ss_zones:';

// Sibling account-data caches owned by other modules. We do not read/write these
// here, but they belong to a single user and must be purged when identity changes
// so a shared browser never leaves one account's business data readable to the
// next. (Pure UI-preference caches like desktop_view / onboarding flags are
// intentionally left alone — they are per-user keyed and non-sensitive.)
export const USER_SCOPED_DATA_PREFIXES = [
  TRACKED_DAS_CACHE_PREFIX,
  WATCH_ZONES_CACHE_PREFIX,
  'ss_pro_detail_cache_v4:',
  'ss_pipeline_stats_cache_v1:',
  'ss_dashboard_apps_cache_v1:',
  'ss_subscription_cache_v1:',
];

// Legacy unscoped account keys from before caches were namespaced. These are
// deleted (never migrated — a blob on a shared browser cannot be safely
// attributed to the current user, and migrating it is exactly the cross-account
// leak WP-19 fixes; the authoritative Supabase load repopulates the correct
// per-user data).
export const LEGACY_UNSCOPED_ACCOUNT_KEYS = ['ss_tracked', 'ss_zones'];

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function scopedKey(prefix, userId) {
  return userId ? `${prefix}${userId}` : null;
}

function readScopedList(prefix, userId) {
  const ls = storage();
  const key = scopedKey(prefix, userId);
  if (!ls || !key) return [];
  try {
    const raw = ls.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeScopedList(prefix, userId, list) {
  const ls = storage();
  const key = scopedKey(prefix, userId);
  if (!ls || !key) return; // never write an unscoped account key
  try {
    ls.setItem(key, JSON.stringify(Array.isArray(list) ? list : []));
  } catch {
    // localStorage is best-effort only; never break the app over quota/private mode.
  }
}

// --- Tracked DAs cache ---
export function readTrackedCache(userId) {
  return readScopedList(TRACKED_DAS_CACHE_PREFIX, userId);
}
export function writeTrackedCache(userId, tracked) {
  writeScopedList(TRACKED_DAS_CACHE_PREFIX, userId, tracked);
}

// --- Watch zones cache ---
export function readZonesCache(userId) {
  return readScopedList(WATCH_ZONES_CACHE_PREFIX, userId);
}
export function writeZonesCache(userId, zones) {
  writeScopedList(WATCH_ZONES_CACHE_PREFIX, userId, zones);
}

// Delete every legacy unscoped account blob on authenticated load. Neither blob
// can be attributed safely on a shared browser, so both are discarded and the
// authoritative Supabase load repopulates the current user's state.
export function removeLegacyAccountCaches() {
  const ls = storage();
  if (!ls) return;
  for (const key of LEGACY_UNSCOPED_ACCOUNT_KEYS) {
    try { ls.removeItem(key); } catch { /* ignore */ }
  }
}

// Purge account-scoped caches.
//   purgeUserScopedCaches(userId) — purge only that user's keys (used on identity
//     change, so the newly signed-in user's freshly written cache is preserved).
//   purgeUserScopedCaches()       — purge every user's account caches (used on
//     sign-out).
// Legacy unscoped account keys are always removed.
export function purgeUserScopedCaches(userId) {
  const ls = storage();
  if (!ls) return;

  let keys = [];
  try {
    keys = Object.keys(ls);
  } catch {
    keys = [];
  }

  for (const key of keys) {
    const matchesPrefix = USER_SCOPED_DATA_PREFIXES.some(prefix => key.startsWith(prefix));
    if (!matchesPrefix) continue;
    // When scoped to a single user, only remove that user's suffix.
    if (userId && !key.endsWith(`:${userId}`)) continue;
    try { ls.removeItem(key); } catch { /* ignore */ }
  }

  for (const legacyKey of LEGACY_UNSCOPED_ACCOUNT_KEYS) {
    try { ls.removeItem(legacyKey); } catch { /* ignore */ }
  }
}
